import { Suspense, useState, useRef, useEffect } from 'react';
import { STRINGS } from '../../Constants/ConstantStrings';
import { ReleaseNotesPopUpWrapper } from '../../SharedComponets/TopNavigation/styles';
import { releaseIcon } from '../../Universal/Assets';
import { useReduxDispatch, useReduxSelector } from '../../Store/reduxHooks';
import { getReleaseNotes } from '../../Store/reducers/ReleaseNotes';
import {
  setProfileOpen,
  setReleaseNotesOpen
} from '../../Store/reducers/Common';
import PALoader from '../../SharedComponets/PALoader';
import dayjs from 'dayjs';
import { Button } from '../../Universal/NovusDSImports';
import {
  btnStyles,
  modalStyles
} from '../../Universal/NovusDSImports/variants';
import { useClickOutside } from '../../Hooks/useClickOutside';
import DemoVideos from './DemoVideos';
import ViewModal from './ViewModal';
import { TabItems } from '../globalStyles';
import DOMPurify from 'dompurify';
import { RootState } from '../../store';

export const ReleaseNotes = () => {
  const dispatch = useReduxDispatch();

  const [releaseNotesWrapper, setReleaseNotesWrapper] =
    useState<boolean>(false);

  const isMobileScreen = useReduxSelector(
    (state: RootState) => state.Common.isMobileScreen
  );

  const isReleaseNotesOpen = useReduxSelector(
    (state: RootState) => state.Common.isReleaseNotesOpen
  );

  const [isViewMoreModalOpen, setIsViewMoreModalOpen] =
      useState<boolean>(false),
    [selectedReleaseNote, setSelectedReleaseNote] = useState<any>(null),
    releaseNotesData = useReduxSelector(
      (state) => state.ReleaseNotes.releaseNotes
    ),
    isFetchingReleaseNotes = useReduxSelector(
      (state) => state.ReleaseNotes.isFetchingReleaseNotes
    );
  const [activeTab, setActiveTab] = useState<'RELEASE_NOTES' | 'DEMO_VIDEOS'>(
    'RELEASE_NOTES'
  );

  const customStyles = {
    content: {
      maxWidth: isMobileScreen ? '90%' : '60%',
      height: '80%'
    }
  };

  const fetchReleaseNotes = () => {
    dispatch(getReleaseNotes({}));
    setReleaseNotesWrapper(!releaseNotesWrapper);
    dispatch(setReleaseNotesOpen({ isReleaseNotesOpen: !isReleaseNotesOpen }));
    dispatch(setProfileOpen({ isProfileOpen: false }));
  };
  const [isDemoVideoModalOpen, setIsDemoVideoModalOpen] =
    useState<boolean>(false);

  const containerRef = useRef(null);
  const excludedElement = useRef(null);
  const demoVideosRef = useRef(null);
  useClickOutside(
    containerRef,
    () => {
      if (
        !(releaseNotesWrapper && (isViewMoreModalOpen || isDemoVideoModalOpen))
      ) {
        if (releaseNotesWrapper) {
          setReleaseNotesWrapper(false);
          setActiveTab('RELEASE_NOTES');
          dispatch(setReleaseNotesOpen({ isReleaseNotesOpen: false }));
        }
        if (isViewMoreModalOpen) {
          setIsViewMoreModalOpen(false);
          setSelectedReleaseNote(null);
        }

        if (isDemoVideoModalOpen) {
          setIsDemoVideoModalOpen(false);
        }
      }
    },
    [excludedElement, demoVideosRef]
  );

  const renderVersionDetails = (item: any) => {
    return (
      <>
        <div className="version-details">
          <div className="version d-flex flex-row justify-content-between align-items-center">
            <span className="version-label">{STRINGS.VERSION}</span>:{' '}
            <span className="version-value">V{item.version}</span>
            <div className="version-release-date">
              {dayjs(item.created).format('DD/MM/YY hh:mm A')}
            </div>{' '}
            <br />
          </div>
          <div
            className="version-release-description"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(item.release_notes)
            }}
          ></div>
          <div
            tabIndex={0}
            className={`read-more ${
              selectedReleaseNote?.id === item.id ? 'active' : ''
            }`}
            onClick={() => {
              setIsViewMoreModalOpen(true);
              setSelectedReleaseNote(item);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsViewMoreModalOpen(true);
                setSelectedReleaseNote(item);
              }
            }}
          >
            {STRINGS.VIEW_MORE}
          </div>
        </div>
      </>
    );
  };

  const onCloseModal = () => {
    setIsViewMoreModalOpen(false);
    setSelectedReleaseNote(null);
  };

  const renderFooter = () => {
    return (
      <Button
        onClick={onCloseModal}
        {...btnStyles.primary}
        className="view-more-close-button"
      >
        {STRINGS.CLOSE}
      </Button>
    );
  };

  return (
    <>
      <div
        className="cursor-pointer"
        data-tooltip-id="release-notes"
        onClick={fetchReleaseNotes}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fetchReleaseNotes();
          }
        }}
        role="button"
        ref={containerRef}
        title={STRINGS.RELEASE_NOTES}
      >
        <img
          role="button"
          tabIndex={0}
          alt="release-notes"
          src={releaseIcon}
        ></img>
      </div>
      {isReleaseNotesOpen && (
        <ReleaseNotesPopUpWrapper
          className={`animate__animated animate__fadeIn animate__faster p-0 ${
            isFetchingReleaseNotes ? 'hide-scrollbar' : 'show-scrollbar'
          }`}
          ref={excludedElement}
        >
          <div className="release-notes-header">{STRINGS.RELEASE_NOTES}</div>
          <div
            className="d-flex flex-row gap-3 align-items-center release-notes-header"
            role="tablist"
          >
            <TabItems
              tabIndex={0}
              role="tab"
              onClick={() => setActiveTab('RELEASE_NOTES')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setActiveTab('RELEASE_NOTES');
                }
              }}
              selectedtab={activeTab === 'RELEASE_NOTES'}
            >
              {STRINGS.RELEASE_NOTES}
            </TabItems>
            <TabItems
              role="tab"
              tabIndex={0}
              onClick={() => {
                setActiveTab('DEMO_VIDEOS');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setActiveTab('DEMO_VIDEOS');
                }
              }}
              selectedtab={activeTab === 'DEMO_VIDEOS'}
            >
              {STRINGS.DEMO_VIDEOS}
            </TabItems>
          </div>

          {activeTab === 'DEMO_VIDEOS' ? (
            <div ref={demoVideosRef} className="overflow-auto">
              <DemoVideos
                isDemoVideoModalOpen={isDemoVideoModalOpen}
                setIsDemoVideoModalOpen={setIsDemoVideoModalOpen}
              />
            </div>
          ) : isFetchingReleaseNotes ? (
            <PALoader />
          ) : releaseNotesData?.results?.length ? (
            <div className="overflow-auto">
              <div className="sub-header">{STRINGS.LATEST_VERSION}</div>

              {renderVersionDetails(releaseNotesData?.results[0])}

              {releaseNotesData?.results?.length > 1 ? (
                <>
                  <div className="sub-header">{STRINGS.OLD_VERSION}</div>
                  {releaseNotesData?.results?.map(
                    (item: any, index: number) => {
                      if (!index) return null;
                      return renderVersionDetails(item);
                    }
                  )}
                </>
              ) : null}
            </div>
          ) : (
            <div className="no-release-notes">{STRINGS.NO_RELEASE_NOTES}</div>
          )}
        </ReleaseNotesPopUpWrapper>
      )}

      {isViewMoreModalOpen && (
        <Suspense fallback={<PALoader />}>
          <ViewModal
            isBodyPaddingNone={true}
            isOpen={isViewMoreModalOpen}
            closeModal={onCloseModal}
            selectedReleaseNote={selectedReleaseNote}
            showFooter={renderFooter()}
            activeTab={activeTab}
            customFooter
            title={`Version: V${selectedReleaseNote?.version}`}
            showHeader={true}
            nocloseIcon={true}
            {...modalStyles}
            style={customStyles}
          />
        </Suspense>
      )}
    </>
  );
};
